import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MAX_VOICE_NOTE_MS,
  VOICE_NOTE_BITRATE,
  baseMimeType,
  clampVoiceNoteDuration,
  isValidVoiceNoteDuration,
  pickVoiceNoteMimeType,
} from '../helpers/voiceNoteHelper';

/**
 * Grabación de notas de voz para el chat sin conexión (KAN-225).
 *
 * Push-to-talk: start() al apretar el micrófono, stop() al soltar. Corta solo a
 * los 3 minutos (maxReached) y descarta grabaciones de menos de 1 s. Siempre
 * libera el micrófono al terminar. La nota se entrega por onComplete.
 * Spec: docs/superpowers/specs/2026-09-15-notas-de-voz-offline-chat-design.md
 */

export interface RecordedVoiceNote {
  blob: Blob;
  /** Mimetype base, ej. 'audio/webm' (sin codecs). */
  mimeType: string;
  durationMs: number;
}

interface UseVoiceNoteRecorderOptions {
  onComplete: (note: RecordedVoiceNote) => void;
}

const TICK_MS = 250;

type RecorderCtor = typeof MediaRecorder;

const getRecorderCtor = (): RecorderCtor | null => {
  if (typeof window === 'undefined') return null;
  const ctor = (window as unknown as { MediaRecorder?: RecorderCtor }).MediaRecorder
    ?? (globalThis as unknown as { MediaRecorder?: RecorderCtor }).MediaRecorder;
  if (!ctor || typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return null;
  return ctor;
};

export function useVoiceNoteRecorder({ onComplete }: UseVoiceNoteRecorderOptions) {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [maxReached, setMaxReached] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startingRef = useRef(false);
  const cancelStartRef = useRef(false);
  const discardRef = useRef(false);
  const mountedRef = useRef(true);

  const clearTimers = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null; }
  };

  const releaseStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const stop = useCallback(() => {
    if (startingRef.current) {
      // Soltó antes de que el navegador abriera el micrófono.
      cancelStartRef.current = true;
      return;
    }
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
  }, []);

  const start = useCallback(async () => {
    const Ctor = getRecorderCtor();
    if (!Ctor || recorderRef.current || startingRef.current) return;
    setError(null);
    setMaxReached(false);
    startingRef.current = true;
    cancelStartRef.current = false;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      startingRef.current = false;
      const name = (err as { name?: string })?.name;
      if (mountedRef.current) {
        setError(name === 'NotAllowedError' || name === 'SecurityError'
          ? 'Permiso de micrófono denegado. Habilita el micrófono en tu navegador.'
          : 'No se pudo acceder al micrófono. Revisá que esté conectado.');
      }
      return;
    }
    startingRef.current = false;
    streamRef.current = stream;
    if (cancelStartRef.current || !mountedRef.current) {
      cancelStartRef.current = false;
      releaseStream();
      return;
    }

    const mimeType = pickVoiceNoteMimeType((type) => Ctor.isTypeSupported(type));
    let recorder: MediaRecorder;
    try {
      recorder = new Ctor(stream, {
        ...(mimeType ? { mimeType } : {}),
        audioBitsPerSecond: VOICE_NOTE_BITRATE,
      });
    } catch {
      releaseStream();
      setError('Este navegador no permite grabar notas de voz.');
      return;
    }

    chunksRef.current = [];
    discardRef.current = false;
    recorder.ondataavailable = (event: { data: Blob }) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      clearTimers();
      releaseStream();
      recorderRef.current = null;
      const durationMs = clampVoiceNoteDuration(Date.now() - startedAtRef.current);
      const chunks = chunksRef.current;
      chunksRef.current = [];
      if (mountedRef.current) {
        setIsRecording(false);
        setElapsedMs(0);
      }
      if (discardRef.current || !isValidVoiceNoteDuration(durationMs)) return;
      const type = baseMimeType(recorder.mimeType || mimeType || '');
      const blob = new Blob(chunks, { type });
      if (blob.size === 0) return;
      onCompleteRef.current({ blob, mimeType: type, durationMs });
    };

    recorderRef.current = recorder;
    startedAtRef.current = Date.now();
    recorder.start();
    setIsRecording(true);
    setElapsedMs(0);
    tickRef.current = setInterval(() => {
      setElapsedMs(Math.min(Date.now() - startedAtRef.current, MAX_VOICE_NOTE_MS));
    }, TICK_MS);
    maxTimerRef.current = setTimeout(() => {
      setMaxReached(true);
      if (recorder.state !== 'inactive') recorder.stop();
    }, MAX_VOICE_NOTE_MS);
  }, []);

  // Al desmontar: descartar lo que se estuviera grabando y soltar el micrófono.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      discardRef.current = true;
      clearTimers();
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        try { recorder.stop(); } catch { /* ignore */ }
      }
      releaseStream();
    };
  }, []);

  return {
    isSupported: getRecorderCtor() !== null,
    isRecording,
    elapsedMs,
    maxReached,
    error,
    start,
    stop,
  };
}
